import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate } from "react-router";

import Input from "../../../../../../components/Form/Input/input";
import { TextArea } from "../../../../../../components/Form/Input/textArea";
import Label from "../../../../../../components/Form/Label";
import { Label as LabelText } from "../../../../../../components/Form/Label/styles";
import PhoneNumberInput, {
  isPossibleWhatsappNumber,
  sanitizePhoneDigits,
} from "../../../../../../components/Form/PhoneNumberInput";
import { Form, FormGroup } from "../../../../../../components/Form/styles";
import { Box } from "../../../../../../components/Layout";
import { OptionTiles } from "../../../../../../components/OptionTiles";
import SaveBar from "../../../../../../components/SaveBar";
import { SubHeader } from "../../../../../../components/SubHeader";
import ToggleSwitch from "../../../../../../components/ToggleSwitch";
import { withPage } from "../../../../../../hooks/CurrentPage";
import { useQueryUrl } from "../../../../../../hooks/QueryUrl";
import {
  PRICING_MAX_BUTTON_TEXT,
  PRICING_MAX_FEATURED_LABEL,
  PRICING_MAX_INSTALLMENTS,
  PRICING_MAX_ITEMS,
  PRICING_MAX_MESSAGE,
  PRICING_MAX_TITLE,
  PricingItem,
} from "../../../../../../model/site";
import { localeOf } from "../../../../../../services/i18ln/format";
import { clone, deepEqual } from "../../../../../../utils/objects";
import { sanitizeUrl } from "../../../../../../utils/url";
import { uuidv4 } from "../../../../../../utils/uuid";
import { ItemThumbnail } from "../../../../components/ItemThumbnail";
import { useItemPreview } from "../../../../hooks/Section";
import { Warning } from "../../../Form/pages/Field/styles";
import { Card } from "../../../Form/pages/styles";
import { navigationState } from "../../../Sections/navigationState";
import { PricingNavigationState } from "../../pricingNavigationState";
import { BenefitRow, BenefitsList } from "./components/BenefitsList";
import { ButtonTypeIcon } from "./components/ButtonTypeIcon";
import { ButtonTypes, Container, FeaturedRow, Field, Pair, ThumbnailBox } from "./styles";

const prefix = "pages.editor.pricing.item";

const BUTTON_TYPES = ["LINK", "WHATSAPP"] as const;

const newBenefitRow = (): BenefitRow => ({ id: uuidv4(), text: "" });

type Money = "price" | "oldPrice" | "installmentPrice";

/** The largest value a plan takes, in cents (999.999.999,99; the page writes a long value smaller). */
export const PRICING_MAX_CENTS = 99999999999;

// A value as its field writes it: money with the separators of the language of the editor ("1.234,56")
const moneyText = (value: number | undefined, language: string) =>
  value === undefined
    ? ""
    : new Intl.NumberFormat(localeOf(language), {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value);

/**
 * What was typed in a field of money as the value it means: only the digits count, coming in by the cents
 * (as a bank app takes them), and those that would go over the largest value are left out.
 * Erasing the last digit of a value that is down to zeros ("0,05" to "0,0") empties the field, as `previous`
 * (the text the field had) tells; a zero typed into it is a value, for a free plan.
 */
export const moneyFromTyping = (text: string, previous = ""): number | undefined => {
  if (!/\d/.test(text)) {
    return undefined;
  }
  let digits = text.replace(/\D/g, "").replace(/^0+/, "");
  const erased = text.length < previous.length && previous.startsWith(text) && /[.,]/.test(text);
  if (!digits && erased) {
    return undefined;
  }
  digits = digits || "0";
  while (Number(digits) > PRICING_MAX_CENTS) {
    digits = digits.slice(0, -1);
  }
  return Number(digits) / 100;
};

// A link the button can open: an address of the web, completed with https:// when it comes without
const buttonLink = (link?: string) => {
  const sanitized = sanitizeUrl(link?.trim());
  return sanitized && /^https?:\/\/[^\s/.]+\.[^\s]+$/i.test(sanitized) ? sanitized : undefined;
};

/** The price divided by the installments, to the cent; undefined without both. */
const dividedPrice = (price?: number, installments?: number) =>
  price !== undefined && installments && installments > 1 ? Math.round((price * 100) / installments) / 100 : undefined;

const newPlan = (): PricingItem => ({
  id: uuidv4(),
  title: "",
  thumbnailType: "EMOJI",
  emoji: "",
  benefits: [],
  buttonText: "",
  buttonType: "LINK",
});

const ItemPricing = ({ state }: { state: PricingNavigationState }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const headerTitle = useRef(t(`${prefix}.header.title.${state.editingPlan?.id ? "edit" : "add"}`));
  // the message the WhatsApp of the plan says when none is written, naming the plan: it is only suggested here
  // (the page writes it, in the language of the Keepo)
  const defaultMessage = (title = "") =>
    title.trim()
      ? t(`${prefix}.form.defaultMessage`, { title: title.trim() })
      : t(`${prefix}.form.defaultMessageNoTitle`);

  // a new plan goes to the WhatsApp another plan of the section already goes to
  const startingPlan = (): PricingItem => {
    const withWhatsapp = [...(state.currentSection.items || [])].reverse().find(plan => plan.whatsapp?.trim());
    return withWhatsapp ? { ...newPlan(), buttonType: "WHATSAPP", whatsapp: withWhatsapp.whatsapp } : newPlan();
  };

  // the plan as the screen opens it: a message already written stays, else the one naming the plan
  const initialItemState = useRef<PricingItem>(
    (() => {
      const base = clone<PricingItem>(state.editingPlan || startingPlan());
      return { ...base, benefits: base.benefits || [] };
    })()
  );

  const [item, setItem] = useState<PricingItem>(initialItemState.current);

  const setTitle = (title: string) => setItem(current => ({ ...current, title }));
  const setMessage = (message: string) => setItem(current => ({ ...current, message }));
  // what is typed in the fields of the values, as typed (the plan keeps the numbers)
  const [moneyTexts, setMoneyTexts] = useState<Record<Money, string>>(() => ({
    price: moneyText(item.price, i18n.language),
    oldPrice: moneyText(item.oldPrice, i18n.language),
    installmentPrice: moneyText(item.installmentPrice, i18n.language),
  }));

  // the benefits as the list edits them, each with an id the dragging follows; the plan keeps the texts
  const [benefitRows, setRows] = useState<BenefitRow[]>(() =>
    (item.benefits || []).map(text => ({ id: uuidv4(), text }))
  );
  const setBenefitRows = (rows: BenefitRow[]) => {
    setRows(rows);
    setItem(current => ({ ...current, benefits: rows.map(({ text }) => text) }));
  };

  useItemPreview(state.currentSection, item, existing => existing.id === item.id);

  // a button with a text needs somewhere to go: a link of the web, or a WhatsApp number that can exist
  const buttonProblem = !item.buttonText.trim()
    ? undefined
    : item.buttonType === "LINK"
      ? buttonLink(item.link)
        ? undefined
        : t(`${prefix}.form.linkMissing`)
      : isPossibleWhatsappNumber(item.whatsapp)
        ? undefined
        : t(`${prefix}.form.whatsappMissing`);
  // the value of an installment is for two installments or more
  const hasInstallments = (item.installments ?? 0) >= 2;
  // a new plan doesn't fit in a section that already has the most plans (the list doesn't open this screen then)
  const isFull =
    !state.currentSection.items?.some(plan => plan.id === item.id) &&
    (state.currentSection.items?.length ?? 0) >= PRICING_MAX_ITEMS;
  const isSaveDisabled = !item.title.trim() || item.price === undefined || !!buttonProblem || isFull;
  const hasUnsavedChanges = () => !deepEqual(initialItemState.current, item);

  // the value of an installment goes with the price and the installments, until it is typed (with interest
  // it isn't the price divided); a plan that already has another value had it typed
  const [installmentTyped, setInstallmentTyped] = useState(
    () =>
      initialItemState.current.installmentPrice !== undefined &&
      initialItemState.current.installmentPrice !==
        dividedPrice(initialItemState.current.price, initialItemState.current.installments)
  );

  // the plan with what changed and, unless typed, the value of an installment worked out again
  const update = (change: Partial<PricingItem>, typedInstallment = installmentTyped) => {
    const next: PricingItem = { ...item, ...change };
    if (!typedInstallment && ("price" in change || "installments" in change)) {
      next.installmentPrice = dividedPrice(next.price, next.installments);
      setMoneyTexts(current => ({ ...current, installmentPrice: moneyText(next.installmentPrice, i18n.language) }));
    }
    (["price", "oldPrice", "installmentPrice", "installments"] as const).forEach(key => {
      if (next[key] === undefined) delete next[key];
    });
    setItem(next);
  };

  const setMoney = (key: Money, text: string) => {
    const value = moneyFromTyping(text, moneyTexts[key]);
    setMoneyTexts(current => ({ ...current, [key]: moneyText(value, i18n.language) }));
    if (key === "installmentPrice") {
      // typed: no longer worked out (an empty field goes back to being worked out, on saving or with the price)
      setInstallmentTyped(value !== undefined);
    }
    update({ [key]: value });
  };

  const setInstallments = (text: string) => {
    const typed = Number.parseInt(text.replace(/\D/g, ""), 10);
    update({ installments: typed >= 1 ? Math.min(typed, PRICING_MAX_INSTALLMENTS) : undefined });
  };

  // the badge text is only stored when typed: without one the page writes "most popular" in the language of
  // the Keepo; a plan that stops being featured forgets it
  const setFeatured = (featured: boolean) =>
    setItem(current => {
      const next = { ...current, featured };
      if (!featured) {
        delete next.featured;
        delete next.featuredLabel;
      }
      return next;
    });

  const saveHandler = () => {
    if (isFull) return;
    const plan: PricingItem = {
      ...item,
      title: item.title.trim(),
      benefits: item.benefits.map(benefit => benefit.trim()).filter(Boolean),
      buttonText: item.buttonText.trim(),
    };
    const featuredLabel = plan.featuredLabel?.trim();
    if (featuredLabel) {
      plan.featuredLabel = featuredLabel;
    } else {
      delete plan.featuredLabel;
    }
    // only what the chosen kind of button uses
    // installments are one or more (the page shows them from two): without them, no value of each
    if ((plan.installments ?? 0) < 2) {
      delete plan.installments;
      delete plan.installmentPrice;
    } else if (plan.installmentPrice === undefined) {
      // the value left empty is the price divided
      const divided = dividedPrice(plan.price, plan.installments);
      if (divided !== undefined) plan.installmentPrice = divided;
    }
    if (plan.buttonType === "LINK") {
      const link = buttonLink(plan.link);
      delete plan.whatsapp;
      delete plan.message;
      if (link) {
        plan.link = link;
      } else {
        delete plan.link;
      }
    } else {
      delete plan.link;
      plan.whatsapp = sanitizePhoneDigits(plan.whatsapp);
      // only a message that was written is kept
      const message = plan.message?.trim();
      if (message) {
        plan.message = message;
      } else {
        delete plan.message;
      }
    }

    const items = state.currentSection.items || [];
    const index = items.findIndex(existing => existing.id === initialItemState.current.id);
    if (index >= 0) {
      items[index] = plan;
    } else {
      items.push(plan);
    }
    state.currentSection.items = items;
    navigate(-1);
  };

  return (
    <Container>
      <SubHeader
        title={headerTitle.current}
        crumbs={[
          { label: t("pages.editor.preview.footer.actions.sections"), to: "/editor/sections" },
          { label: t("pages.editor.pricing.header.title"), to: "/editor/pricing" },
        ]}
        hasUnsavedChanges={hasUnsavedChanges}
      />
      <Box>
        <ThumbnailBox>
          <ItemThumbnail
            types={["IMAGE", "EMOJI"]}
            thumbnailType={item.thumbnailType === "IMAGE" ? "IMAGE" : "EMOJI"}
            onThumbnailChange={thumbnailType => setItem({ ...item, thumbnailType: thumbnailType as "IMAGE" | "EMOJI" })}
            image={item.image}
            emoji={item.emoji}
            onChangeEmoji={emoji => setItem({ ...item, emoji })}
            onChangeImage={image => setItem({ ...item, image })}
          />
        </ThumbnailBox>
        <Form onSubmit={event => event.preventDefault()}>
          <FormGroup>
            <Input
              id="title"
              required
              type="text"
              value={item.title}
              maxLength={PRICING_MAX_TITLE}
              onChange={e => setTitle(e.target.value)}
              label={t(`${prefix}.form.title`)}
            />
          </FormGroup>

          <Field>
            <FeaturedRow>
              <div className="text">
                <h6>{t(`${prefix}.form.featured`)}</h6>
                <p className="description">{t(`${prefix}.form.featuredHint`)}</p>
              </div>
              <ToggleSwitch label={t(`${prefix}.form.featured`)} value={!!item.featured} setValue={setFeatured} />
            </FeaturedRow>
            {item.featured && (
              <FormGroup>
                <Input
                  id="featuredLabel"
                  type="text"
                  value={item.featuredLabel || ""}
                  maxLength={PRICING_MAX_FEATURED_LABEL}
                  placeholder={t(`${prefix}.form.featuredDefault`)}
                  onChange={e => setItem({ ...item, featuredLabel: e.target.value })}
                  label={t(`${prefix}.form.featuredLabel`)}
                />
              </FormGroup>
            )}
          </Field>

          <Field>
            <Pair>
              <FormGroup>
                <Input
                  id="price"
                  required
                  type="text"
                  value={moneyTexts.price}
                  onChange={e => setMoney("price", e.target.value)}
                  label={t(`${prefix}.form.price`)}
                />
              </FormGroup>
              <FormGroup>
                <Input
                  id="oldPrice"
                  type="text"
                  value={moneyTexts.oldPrice}
                  onChange={e => setMoney("oldPrice", e.target.value)}
                  label={t(`${prefix}.form.oldPrice`)}
                />
              </FormGroup>
            </Pair>
            <p className="hint">{t(`${prefix}.form.pricesHint`)}</p>
          </Field>

          <Field>
            <Pair>
              <FormGroup>
                <Input
                  id="installments"
                  type="text"
                  maxLength={String(PRICING_MAX_INSTALLMENTS).length}
                  ignoreLengthInfo
                  value={item.installments === undefined ? "" : String(item.installments)}
                  onChange={e => setInstallments(e.target.value)}
                  label={t(`${prefix}.form.installments`)}
                />
              </FormGroup>
              <FormGroup>
                <Input
                  id="installmentPrice"
                  type="text"
                  disabled={!hasInstallments}
                  value={hasInstallments ? moneyTexts.installmentPrice : ""}
                  onChange={e => setMoney("installmentPrice", e.target.value)}
                  label={t(`${prefix}.form.installmentPrice`)}
                />
              </FormGroup>
            </Pair>
            <p className="hint">{t(`${prefix}.form.installmentsHint`)}</p>
          </Field>

          <Field>
            <LabelText as="span" className="caption">
              {t(`${prefix}.form.benefits`)}
            </LabelText>
            <Card>
              <BenefitsList rows={benefitRows} onChange={setBenefitRows} newRow={newBenefitRow} />
            </Card>
          </Field>

          <Field>
            <LabelText as="span" className="caption">
              {t(`${prefix}.form.button`)}
            </LabelText>
            <FormGroup>
              <Input
                id="buttonText"
                type="text"
                value={item.buttonText}
                maxLength={PRICING_MAX_BUTTON_TEXT}
                onChange={e => setItem({ ...item, buttonText: e.target.value })}
                label={t(`${prefix}.form.buttonText`)}
              />
            </FormGroup>
            <LabelText as="span" className="caption" id="button-type-label">
              {t(`${prefix}.form.buttonType.label`)}
            </LabelText>
            <ButtonTypes>
              <OptionTiles $columns={2} $compact role="radiogroup" aria-labelledby="button-type-label">
                {BUTTON_TYPES.map(value => (
                  <button
                    type="button"
                    role="radio"
                    key={value}
                    aria-checked={item.buttonType === value}
                    onClick={() => setItem({ ...item, buttonType: value })}>
                    <ButtonTypeIcon type={value} />
                    {t(`${prefix}.form.buttonType.${value}`)}
                  </button>
                ))}
              </OptionTiles>
            </ButtonTypes>
            {item.buttonType === "LINK" ? (
              <FormGroup>
                <Input
                  id="link"
                  type="text"
                  value={item.link || ""}
                  autoCapitalize="off"
                  onChange={e => setItem({ ...item, link: e.target.value })}
                  label={t(`${prefix}.form.link`)}
                />
                {buttonProblem && <Warning>{buttonProblem}</Warning>}
              </FormGroup>
            ) : (
              <>
                <FormGroup>
                  <PhoneNumberInput
                    id="whatsapp"
                    value={item.whatsapp}
                    label={t(`${prefix}.form.whatsapp`)}
                    onChange={whatsapp => setItem({ ...item, whatsapp })}
                  />
                  {buttonProblem && <Warning>{buttonProblem}</Warning>}
                </FormGroup>
                <FormGroup>
                  <TextArea
                    id="message"
                    style={{ height: "6rem" }}
                    maxLength={PRICING_MAX_MESSAGE}
                    value={item.message || ""}
                    placeholder={defaultMessage(item.title)}
                    onChange={e => setMessage(e.target.value)}
                  />
                  <Label
                    htmlFor="message"
                    maxLength={PRICING_MAX_MESSAGE}
                    usedLength={item.message?.length || 0}
                    text={t(`${prefix}.form.message`)}
                  />
                </FormGroup>
              </>
            )}
          </Field>
        </Form>
        {isFull && <Warning>{t("pages.editor.pricing.form.limit", { count: PRICING_MAX_ITEMS })}</Warning>}
      </Box>
      <SaveBar onSave={saveHandler} disabled={isSaveDisabled} hasUnsavedChanges={hasUnsavedChanges} />
    </Container>
  );
};

/** The screen of one plan. Reached from the list of the section, which hands it the section. */
const ItemPricingPage = () => {
  const state = navigationState.get() as PricingNavigationState | undefined;
  const page = useQueryUrl().get("page");

  // opened by its address (a reload): there's no section to add the plan to
  if (state?.currentSection?.type !== "pricing") {
    return <Navigate to={withPage("/editor/sections", page)} replace />;
  }
  return <ItemPricing state={state} />;
};

export default ItemPricingPage;
